import { normalizePhoneNumber } from "../../utils/phoneUtils";
import { makeRequest } from "./client";

/** Infer MIME from a data URL for upload handlers that expect separate contentType. */
function mimeFromDataUrl(dataUrl: string): string | undefined {
  const m = dataUrl.match(/^data:([^;]+);base64,/);
  return m?.[1]?.trim();
}

const extractMemberCount = (
  payload: Partial<{
    memberCount: number | string;
    membersCount: number | string;
    totalMembers: number | string;
    userCount: number | string;
    member_count: number | string;
  }>
): number => {
  const rawValue =
    payload.memberCount ??
    payload.membersCount ??
    payload.totalMembers ??
    payload.userCount ??
    payload.member_count;
  const parsed = Number(rawValue);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : 0;
};

const toBoolean = (value: unknown): boolean | null => {
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value === 1;
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    if (["true", "1", "yes", "public"].includes(normalized)) return true;
    if (["false", "0", "no", "private"].includes(normalized)) return false;
  }
  return null;
};

/** Backend exposes subcircle public flag as `isPublic` only (string or boolean). */
const parseSubcircleIsPublic = (payload: {
  isPublic?: string | boolean | number | null;
}): boolean => toBoolean(payload.isPublic) ?? false;

export const circlesApi = {
  createSubCircle: async (
    circleData: {
      name: string;
      description: string;
      isPublic: boolean;
      schoolId: string;
      logo?: string;
      learningPoints: string[];
    },
    token: string
  ): Promise<{
    message: string;
    circle: {
      circleId: string;
      name: string;
      description: string;
      isPublic: boolean;
      isSubCircle: boolean;
      parentCircle: string;
      memberCount: number;
      createdAt: string;
      createdBy: string;
      circleApproved: boolean;
      logo: string;
      type: string;
      learningPoints?: string[];
    };
  }> => {
    const response = await makeRequest<{
      message: string;
      subcircle: {
        subcircleId: string;
        name: string;
        description: string;
        isPublic: boolean | string;
        memberCount?: number | string;
        membersCount?: number | string;
        totalMembers?: number | string;
        userCount?: number | string;
        member_count?: number | string;
        subCircle: {
          id: string;
          parentOrgId: string;
        };
        createdAt: string;
        createdBy: string;
        isApproved: boolean;
        logo: string;
        type: string;
        learningPoints?: string[];
      };
    }>(`/v1/org/${circleData.schoolId}/circles`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
      body: JSON.stringify({
        name: circleData.name,
        description: circleData.description,
        isPublic: circleData.isPublic,
        ...(circleData.logo && { logo: circleData.logo }),
        ...(circleData.learningPoints !== undefined && {
          learningPoints: circleData.learningPoints,
        }),
      }),
    });

    return {
      message: response.message,
      circle: {
        circleId: response.subcircle.subcircleId,
        name: response.subcircle.name,
        description: response.subcircle.description,
        isPublic: parseSubcircleIsPublic(response.subcircle),
        isSubCircle: true,
        parentCircle: response.subcircle.subCircle.parentOrgId,
        memberCount: extractMemberCount(response.subcircle),
        createdAt: response.subcircle.createdAt,
        createdBy: response.subcircle.createdBy,
        circleApproved: response.subcircle.isApproved,
        logo: response.subcircle.logo,
        type: response.subcircle.type,
        learningPoints: Array.isArray(response.subcircle.learningPoints)
          ? response.subcircle.learningPoints
          : undefined,
      },
    };
  },

  getAll: async (
    schoolId: string,
    token: string
  ): Promise<{
    circles: Array<{
      circleId: string;
      name: string;
      description: string;
      isPublic: boolean;
      isSubCircle: boolean;
      parentCircle?: string;
      memberCount: number;
      createdAt: string;
      createdBy: string;
      circleApproved: boolean;
      logo: string;
      type: string;
      orgBanner?: string;
      learningPoints?: string[];
      displayOrder?: number;
    }>;
  }> => {
    const response = await makeRequest<{
      message: string;
      subcircles: Array<{
        subcircleId: string;
        name: string;
        description: string;
        isPublic: boolean | string;
        memberCount?: number | string;
        membersCount?: number | string;
        totalMembers?: number | string;
        userCount?: number | string;
        member_count?: number | string;
        subCircle: {
          id: string;
          parentOrgId: string;
        };
        createdAt: string;
        createdBy: string;
        isApproved: boolean;
        logo: string;
        type: string;
        orgBanner?: string;
        learningPoints?: string[];
        displayOrder?: number;
      }>;
    }>(`/v1/org/${schoolId}/circles`, {
      headers: { Authorization: `Bearer ${token}` },
    });

    const circles = response.subcircles.map((subcircle) => ({
      circleId: subcircle.subcircleId,
      name: subcircle.name,
      description: subcircle.description,
      isPublic: parseSubcircleIsPublic(subcircle),
      isSubCircle: subcircle.subcircleId !== subcircle.subCircle?.parentOrgId,
      parentCircle:
        subcircle.subCircle?.parentOrgId ||
        subcircle.subcircleId.split("-")[0],
      memberCount: extractMemberCount(subcircle),
      createdAt: subcircle.createdAt,
      createdBy: subcircle.createdBy,
      circleApproved: subcircle.isApproved,
      logo: subcircle.logo,
      type: subcircle.type,
      orgBanner:
        typeof subcircle.orgBanner === "string" &&
        subcircle.orgBanner.trim() !== ""
          ? subcircle.orgBanner.trim()
          : undefined,
      learningPoints: Array.isArray(subcircle.learningPoints)
        ? subcircle.learningPoints
        : undefined,
      ...(typeof subcircle.displayOrder === "number"
        ? { displayOrder: subcircle.displayOrder }
        : {}),
    }));

    return { circles };
  },

  editSubCircle: async (
    subcircleData: {
      subcircleId: string;
      schoolId: string;
      name?: string;
      description?: string;
      isPublic?: boolean;
      logo?: string;
      orgBanner?: string;
      contentType?: string;
      learningPoints?: string[];
    },
    token: string
  ): Promise<{
    message: string;
    circle: {
      circleId: string;
      name: string;
      description: string;
      isPublic: boolean;
      isSubCircle: boolean;
      parentCircle: string;
      memberCount: number;
      createdAt: string;
      updatedAt: string;
      createdBy: string;
      circleApproved: boolean;
      logo: string;
      type: string;
      orgBanner?: string;
      learningPoints?: string[];
    };
  }> => {
    const response = await makeRequest<{
      message: string;
      subcircle: {
        subcircleId: string;
        name: string;
        description: string;
        isPublic: boolean | string;
        memberCount?: number | string;
        membersCount?: number | string;
        totalMembers?: number | string;
        userCount?: number | string;
        member_count?: number | string;
        subCircle: {
          id: string;
          parentOrgId: string;
        };
        createdAt: string;
        updatedAt: string;
        createdBy: string;
        isApproved: boolean;
        logo: string;
        type: string;
        orgBanner?: string;
        learningPoints?: string[];
      };
    }>(
      `/v1/org/${subcircleData.schoolId}/circles/${subcircleData.subcircleId}`,
      {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          name: subcircleData.name,
          description: subcircleData.description,
          isPublic: subcircleData.isPublic,
          ...(subcircleData.logo && { logo: subcircleData.logo }),
          ...(subcircleData.orgBanner !== undefined && {
            orgBanner: subcircleData.orgBanner,
          }),
          ...(subcircleData.learningPoints !== undefined && {
            learningPoints: subcircleData.learningPoints,
          }),
          ...(subcircleData.orgBanner?.includes("data:") && {
            contentType:
              subcircleData.contentType ||
              mimeFromDataUrl(subcircleData.orgBanner) ||
              "image/jpeg",
          }),
        }),
      }
    );

    return {
      message: response.message,
      circle: {
        circleId: response.subcircle.subcircleId,
        name: response.subcircle.name,
        description: response.subcircle.description,
        isPublic: parseSubcircleIsPublic(response.subcircle),
        isSubCircle: true,
        parentCircle: response.subcircle.subCircle.parentOrgId,
        memberCount: extractMemberCount(response.subcircle),
        createdAt: response.subcircle.createdAt,
        updatedAt: response.subcircle.updatedAt || "",
        createdBy: response.subcircle.createdBy,
        circleApproved: response.subcircle.isApproved,
        logo: response.subcircle.logo,
        type: response.subcircle.type,
        orgBanner:
          typeof response.subcircle.orgBanner === "string"
            ? response.subcircle.orgBanner.trim() || undefined
            : undefined,
        learningPoints: Array.isArray(response.subcircle.learningPoints)
          ? response.subcircle.learningPoints
          : undefined,
      },
    };
  },

  removeSubCircle: async (
    subcircleId: string,
    schoolId: string,
    token: string
  ): Promise<{
    message: string;
    subcircleId: string;
  }> => {
    const response = await makeRequest<{
      message: string;
      subcircleId: string;
    }>(`/v1/org/${schoolId}/circles/${subcircleId}`, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${token}` },
    });

    return response;
  },

  addUserToSubCircle: async (
    userData: {
      email?: string;
      phoneNumber?: string;
      username?: string;
      subcircleId: string;
      schoolId: string;
      accessLegacyMessages?: boolean;
      legacyAccessDays?: number;
      alias?: string;
    },
    token: string
  ): Promise<{
    message: string;
    status?: "pending" | "linked";
    invitationId?: string;
    userId?: string;
    subcircleId: string;
    schoolId: string;
    role: string;
  }> => {
    const body: any = {
      email:
        userData.email &&
        userData.email.trim() !== "" &&
        userData.email.trim().toUpperCase() !== "N/A"
          ? userData.email.toLowerCase()
          : undefined,
      phoneNumber: userData.phoneNumber
        ? normalizePhoneNumber(userData.phoneNumber)
        : undefined,
      username: userData.username?.trim() || undefined,
      role: "member",
      accessLegacyMessages:
        userData.accessLegacyMessages !== undefined
          ? userData.accessLegacyMessages
          : false,
    };

    if (userData.legacyAccessDays !== undefined) {
      body.legacyAccessDays = userData.legacyAccessDays;
    }

    const aliasTrimmed = userData.alias?.trim();
    if (aliasTrimmed) {
      body.alias = aliasTrimmed;
    }

    const response = await makeRequest<{
      message: string;
      status?: "pending" | "linked";
      invitationId?: string;
      userId?: string;
      subcircleId: string;
      schoolId: string;
      role: string;
    }>(`/v1/org/${userData.schoolId}/circles/${userData.subcircleId}/users`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
      body: JSON.stringify(body),
    });

    return response;
  },

  getSubCircleUsers: async (
    subcircleId: string,
    schoolId: string,
    token: string
  ): Promise<{
    users: Array<{
      userId: string;
      email: string;
      name: string;
      phoneNumber?: string;
      role: string;
      joinedAt: string;
      alias?: string;
    }>;
  }> => {
    const response = await makeRequest<{
      users: Array<{
        userId: string;
        email: string;
        name: string;
        phoneNumber?: string;
        role: string;
        joinedAt: string;
        alias?: string;
      }>;
    }>(`/v1/org/${schoolId}/circles/${subcircleId}/users`, {
      method: "GET",
      headers: { Authorization: `Bearer ${token}` },
    });

    return response;
  },

  removeUserFromSubCircle: async (
    userData: {
      userId: string;
      subcircleId: string;
      schoolId: string;
    },
    token: string
  ): Promise<{
    message: string;
    userId: string;
    subcircleId: string;
  }> => {
    const response = await makeRequest<{
      message: string;
      userId: string;
      subcircleId: string;
    }>(
      `/v1/org/${userData.schoolId}/circles/${userData.subcircleId}/users/${userData.userId}`,
      {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      }
    );

    return response;
  },

  updateUserAliasInSubCircle: async (
    userData: {
      userId: string;
      subcircleId: string;
      schoolId: string;
      alias: string;
    },
    token: string
  ): Promise<{
    message: string;
    userId: string;
    subcircleId: string;
    alias: string | null;
    updatedAt: string;
  }> => {
    return makeRequest<{
      message: string;
      userId: string;
      subcircleId: string;
      alias: string | null;
      updatedAt: string;
    }>(
      `/v1/org/${userData.schoolId}/circles/${userData.subcircleId}/users/${userData.userId}`,
      {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          alias: userData.alias,
        }),
      }
    );
  },

  reorderSubCircles: async (
    schoolId: string,
    orderedSubcircleIds: string[],
    token: string
  ): Promise<{
    message: string;
    orderedSubcircleIds: string[];
  }> => {
    return makeRequest<{
      message: string;
      orderedSubcircleIds: string[];
    }>(`/v1/org/${schoolId}/circles/reorder`, {
      method: "PUT",
      headers: { Authorization: `Bearer ${token}` },
      body: JSON.stringify({ orderedSubcircleIds }),
    });
  },
};
